import {
	AerInputStructure,
	type InputProps,
} from "@aerstudios/aerated-aerinputstructure";
import type { WidgetProps } from "@rjsf/utils";
import cx from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useFormBuilderContext } from "../FormBuilder/FormBuilder.context";
import * as styles from "./TextInput.module.scss";
import { emailRegex, phoneRegex } from "./TextInputs.consts";

export type TextInputProps = WidgetProps<InputProps> & {
	isNumber?: boolean;
	confirmedValue?: string;
};

export const TextInput: React.FC<TextInputProps> = ({
	onChange,
	onFocus,
	onBlur,
	onError: _onError,
	className,
	value,
	autofocus,
	readonly,
	isNumber = false,
	required,
	...aerTextInputProps
}) => {
	// Extract the props we don't want to pass down.
	const {
		uiSchema: _uiSchema,
		formContext: _formContext,
		rawErrors: _rawErrors,
		hideLabel: _hideLabel,
		hideError: _hideError,
		...aerTextInputPropsRest
	} = aerTextInputProps;
	const [inputValue, setInputValue] = useState(value);
	const { hasSubmitted } = useFormBuilderContext();
	const inputRef = useRef<HTMLInputElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Required to allow the user to change the value of the input
	useEffect(() => {
		if ((value || value === "") && inputRef.current) {
			setInputValue(value);
			onChange(value);
			onFocus(aerTextInputProps.id, value);
			onBlur(aerTextInputProps.id, value);
		}
	}, [value]);
	return (
		<>
			<AerInputStructure.Root
				{...aerTextInputPropsRest}
				handleChange={(newValue: string) => {
					setInputValue(newValue);
					onChange(newValue);
				}}
				onFocus={(v) => onFocus(aerTextInputProps.id, v)} // TODO TBC: Mismatch between WidgetProps and AerTextInputProps
				handleBlur={(v) => onBlur(aerTextInputProps.id, v)} // TODO TBC: Mismatch between WidgetProps and AerTextInputProps
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
					}
				}}
				forceValidityCheck={hasSubmitted}
				value={inputValue}
				errorMessage={_rawErrors?.[0]}
			>
				<AerInputStructure.Input
					ref={inputRef}
					id={aerTextInputProps.id}
					{...aerTextInputPropsRest}
					type={
						isNumber
							? "number"
							: aerTextInputProps.options?.textInputType
								? `${aerTextInputProps.options?.textInputType}`
								: "text"
					}
					autoComplete={aerTextInputProps.options?.autocomplete}
					aria-autocomplete="inline"
					aria-label={aerTextInputProps.label}
					min={isNumber ? 0 : undefined}
					readOnly={readonly}
					autoFocus={autofocus}
					disabled={aerTextInputProps.schema?.readOnly}
					className={cx(styles.textInput, className)}
					maxLength={aerTextInputProps.schema?.maxLength}
					minLength={aerTextInputProps.schema?.minLength?.toString()}
					pattern={aerTextInputProps.options?.pattern as string}
					required={required}
				>
					<input type="text" />
				</AerInputStructure.Input>
				<AerInputStructure.ErrorMessage />
			</AerInputStructure.Root>
		</>
	);
};

export const PhoneNumberInput: React.FC<TextInputProps> = ({
	className,
	...aerTextInputProps
}) => {
	return (
		<TextInput
			className={cx(styles.textInput, className)}
			{...aerTextInputProps}
			pattern={phoneRegex}
			type="tel"
			aria-autocomplete="inline"
		/>
	);
};

export const EmailInput: React.FC<TextInputProps> = ({
	className,
	...aerTextInputProps
}) => {
	return (
		<TextInput
			className={cx(styles.textInput, className)}
			{...aerTextInputProps}
			type="email"
			pattern={emailRegex}
			aria-autocomplete="inline"
		/>
	);
};

export const NumberInput: React.FC<TextInputProps> = ({
	className,
	...aerTextInputProps
}) => {
	return (
		<TextInput
			className={cx(styles.textInput, className)}
			{...aerTextInputProps}
			isNumber
			minLength={0}
		/>
	);
};

export const PlainTextInput: React.FC<InputProps> = ({
	label,
	handleChange,
	handleBlur,
	...props
}) => (
	<AerInputStructure.Root handleChange={handleChange} handleBlur={handleBlur}>
		<AerInputStructure.Label>{label}</AerInputStructure.Label>
		<AerInputStructure.Input {...props} className={cx(styles.textInput)}>
			<input type="text" />
		</AerInputStructure.Input>
	</AerInputStructure.Root>
);
export default TextInput;
